import { SpecificAnswers } from './specific-questions';
import { FormGroup, FormControl, Validators, FormArray, AbstractControl, ValidatorFn } from '@angular/forms';

export enum QuestionType {
    Text,
    Checkbox,
    Select,
    RadioButton,
    MultiSelect,
    DatePicker,
    Digits,
    YesNoRadioButton,
    PhoneNumber
}

export const questionType: { [key: number]: string } = {
    [QuestionType.Text]: 'Text',
    [QuestionType.Checkbox]: 'Checkbox',
    [QuestionType.Select]: 'Select',
    [QuestionType.RadioButton]: 'Radio Button',
    [QuestionType.MultiSelect]: 'MultiSelect',
    [QuestionType.DatePicker]: 'DatePicker',
    [QuestionType.Digits]: 'Digits',
    [QuestionType.YesNoRadioButton]: 'Yes/No Radio Button',
    [QuestionType.PhoneNumber]: 'Phone number',
};

export class QuestionOptionView {
    public id: number;
    public questionId: number;
    public text: string;
    public dependentOptionIds: number[];
    public requiredOptionIds: number[];
    constructor(value: any) {
        Object.assign(this, value);
    }
}

export class QuestionView {
    public id?: number;
    public text: string;
    public infoTitle: string;
    public infoDescription: string;
    public value: number;
    public typeLabel: string;
    public type: QuestionType;
    public specification: QuestionSpecification;
    public specificAnswers: SpecificAnswers[];
    public questionOption: QuestionOptionView[];
    constructor(value: any) {
        Object.assign(this, value);
        this.typeLabel = questionType[this.type];
        if (Array.isArray(value.questionOption)) {
            this.questionOption = value.questionOption.map((x: any) => new QuestionOptionView(x));
        }
        if (Array.isArray(value.specificAnswers)) {
            this.specificAnswers = value.specificAnswers.map((x: any) => new SpecificAnswers(x));
        }
    }
}

export class TypeQuestionView {
    public id?: number;
    public isExcluded: boolean;
    public isMoreInsurant: boolean;
    public step: number;
    public order: number;
    public questionId: number;
    public insuranceTypeId: number;
    public parentQuestionId: number;
    public groupSpecification: GroupSpecification;
    public property: number;
    public question: QuestionView;
    public answers: any;
    public requiredOptionsId: number[];
    constructor(value: any) {
        Object.assign(this, value);
    }
}

export enum GroupSpecification {
    None,
    PersonalInfo
}


export class StepView {
    public step: number;
    public name: string;
    public groupSpecification: GroupSpecification;
    constructor(value: any) {
        Object.assign(this, value);
    }
}

export class StepsQuestionsView {
    public maxInsurant: number;
    public specification: number;
    public questions: TypeQuestionView[];
    public specificQuestions: TypeQuestionView[];
    public steps: StepView[];
    constructor(value: any) {
        Object.assign(this, value);
        if (Array.isArray(value.questions)) {
            this.questions = value.questions.map((x: any) => new TypeQuestionView(x));
        }
        if (Array.isArray(value.specificQuestions)) {
            this.specificQuestions = value.specificQuestions.map((x: any) => new TypeQuestionView(x));
        }
        if (Array.isArray(value.steps)) {
            this.steps = value.steps.map((x: any) => new StepView(x));
        }
    }
}

export enum QuestionSpecification {
    None,
    CarCoverType,
    PersonalFirstName,
    PersonalLastName,
    InsuranceStartDate,
    CarVehiclePrice,
    PersonalBirthday,
    PersonalEmail,
    PersonalPhoneNumber,
    Home_CoverType,
    Home_HomePrice,
    Home_ContentPrice,
    Home_AppliancesPrice,
    Home_JewelleryPrice,
    Home_ElectronicsPrice,
    Home_ArtPrice,
    Home_StreetAddress
}

export function ValidateAnswer(control: AbstractControl) {
    if (control.value === null || control.value === []) {
        return { checkboxError: true };
    }
    return null;
}

export class TypeQuestionViewForm {
    public static createTypeQuestionForm(question: TypeQuestionView = null): FormGroup {
        const answerValidators = [Validators.required, ValidateAnswer];
        const specificValidator = this.getSpecificValidator(question.question.specification);
        if (specificValidator) {
            answerValidators.push(specificValidator);
        }

        return new FormGroup({
            id: new FormControl(question ? question.id : null),
            step: new FormControl(question ? question.step : null),
            isMoreInsurant: new FormControl(question ? question.isMoreInsurant : false),
            isExcluded: new FormControl(question ? question.isExcluded : false),
            order: new FormControl(question ? question.order : null),
            questionId: new FormControl(question ? question.questionId : null),
            parentQuestionId: new FormControl(question ? question.parentQuestionId : null),
            groupSpecification: new FormControl(question ? question.groupSpecification : null),
            property: new FormControl(question ? question.property : null),
            answers: new FormControl(question ? question.answers : null, answerValidators),
            insuranceTypeId: new FormControl(question ? question.insuranceTypeId : null),
            requiredOptionsId: new FormControl(question ? question.requiredOptionsId : null),
            question: this.createQuestionViewForm(question ? question.question : null),
            additional: new FormArray([])
        });
    }


    public static createQuestionViewForm(question: QuestionView = null): FormGroup {
        return new FormGroup({
            id: new FormControl(question ? question.id : null),
            text: new FormControl(question ? question.text : '', Validators.required),
            infoTitle: new FormControl(question ? question.infoTitle : ''),
            infoDescription: new FormControl(question ? question.infoDescription : ''),
            type: new FormControl(question ? question.type : null),
            specification: new FormControl(question ? question.specification : null),
            specificAnswers: new FormControl([]),
            questionOption: new FormArray(question && question.questionOption ?
                question.questionOption.map(x => this.createQuestionOptionViewForm(x)) : [])
        });
    }

    public static createQuestionOptionViewForm(question: QuestionOptionView = null): FormGroup {
        return new FormGroup({
            id: new FormControl(question ? question.id : null),
            questionId: new FormControl(question ? question.questionId : null),
            text: new FormControl(question ? question.text : null, Validators.required),
            dependentOptionIds: new FormControl(question ? question.dependentOptionIds : []),
            requiredOptionIds: new FormControl(question ? question.requiredOptionIds : [])
        });
    }

    public static createSpecificAnswersForm(question: SpecificAnswers = null): FormGroup {
        return new FormGroup({
            value: new FormControl(question ? question.value : null),
            carsLabel: new FormControl(question ? question.carsLabel : null),
            text: new FormControl(question ? question.text : ''),
        });
    }

    public static createCustomAnswersForm(): FormGroup {
        return new FormGroup({
            id: new FormControl(null),
            value: new FormControl(null),
        });
    }

    private static getSpecificValidator(specification: QuestionSpecification = QuestionSpecification.None): ValidatorFn {
        switch (specification) {
            case QuestionSpecification.PersonalEmail:
                return Validators.pattern(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,10}$|^$/);
            default:
                return null;
        }
    }

}
